import { DealActions, DealSubscriptionIdentifier, DefaultCollateralIdentifierType, EntityTypeEnum, SubscriptionIdentifier } from 'api'
import React, { useMemo } from 'react'
import { useAppDispatch } from 'utils/redux'
import { useSelector } from 'react-redux'
import { DataGrid, filteredColumnDefsBuilder, GridAutoSizing, GridId } from 'view/components/datagrid'
import { structuredSelector } from 'selectors/structuredSelectors'
import { CenterPanel } from 'view/routes/routesLayout'
import { isAdminGatewayUserSelector } from 'selectors/userSelectors'
import { startConsiderationOverrideEnabledSelector, isStockBorrowingLendingEnabledSelector } from 'selectors/featureSelectors'
import { DealsGridColumnId } from '../deals/components/DealsGridColumnNames'
import { AdminOnlyColumnIds } from '../ColumnNames'
import { StructuredHeroPanel, structuredGridColumnDefs, structuredGridOptions } from './components'
import { RelatedDealsThunks } from 'thunks'
import { defaultCollateralIdentifierTypeSelector } from 'selectors/userPreferenceSelectors'
import { GridApi } from 'ag-grid-community'
import { OrganisationFilterSwitch } from 'view/components/datagrid/toolbar/OrganisationFilterSwitch'
import { IncludeTerminatedToolbarSwitch } from '../openRepos/components/IncludeTerminatedToolbarSwitch'
import { SalesCoverFilterSwitch } from 'view/components/datagrid/toolbar/SalesCoverFilterSwitch'
import { DealsGroupByToggles } from '../deals/components/DealsScreenBase/DealsGroupByToggles'
import { DealSubscriptionFiltersComponent } from 'view/components/datagrid/filters/DealSubscriptionFilters'
import { relatedStructuredDetailsSelector } from 'selectors/dealSelectors'
import { RelatedPanel } from '../deals/components/DealsScreenBase/RelatedPanel'
import { generateExportFileName } from 'view/components/datagrid/gridUtils/utils'
import { useIsCurrentOrganisationBuySide, useIsSnapshotLoaded, useSubscription } from 'view/hooks'

const customToolbarSwitches = [OrganisationFilterSwitch, IncludeTerminatedToolbarSwitch, SalesCoverFilterSwitch]

export const StructuredScreen = () => {
  const dispatch = useAppDispatch()
  const structuredRows = useSelector(structuredSelector)
  const relatedDealDetails = useSelector(relatedStructuredDetailsSelector)
  const subscriptionDetail = useSubscription(EntityTypeEnum.DEAL, SubscriptionIdentifier.STRUCTURED)
  const isAdminUser = useSelector(isAdminGatewayUserSelector)
  const startConsiderationOverrideEnabled = useSelector(startConsiderationOverrideEnabledSelector)
  const stockBorrowingLendingEnabled = useSelector(isStockBorrowingLendingEnabledSelector)
  const defaultCollateralIdentifierType = useSelector(defaultCollateralIdentifierTypeSelector)
  const snapshotLoaded = useIsSnapshotLoaded(EntityTypeEnum.DEAL)
  const isBuySideOrganisation = useIsCurrentOrganisationBuySide()

  const columnDefs = useMemo(
    () =>
      filteredColumnDefsBuilder(structuredGridColumnDefs(isAdminUser, isBuySideOrganisation))
        .withoutIf(!isAdminUser, Object.values(AdminOnlyColumnIds))
        .withoutIf(!startConsiderationOverrideEnabled, [DealsGridColumnId.DEAL_START_CONSIDERATION_OVERRIDE])
        .withoutIf(!stockBorrowingLendingEnabled, [
          DealsGridColumnId.DEAL_FEE,
          DealsGridColumnId.SECONDARY_COLLATERAL_NAME,
          DealsGridColumnId.DEAL_STOCK_BORROWING_LENDING_TYPE
        ])
        .build(),
    [isAdminUser, startConsiderationOverrideEnabled, isBuySideOrganisation, stockBorrowingLendingEnabled]
  )

  const onPageChanged = (pageNumber: number) => {
    if (subscriptionDetail) {
      dispatch(DealActions.pageSubscribe(SubscriptionIdentifier.STRUCTURED, subscriptionDetail.filters, pageNumber))
    }
  }

  const handleRelatedOnClose = () => {
    dispatch(RelatedDealsThunks.updateStructuredRelatedDealsSubscription(null))
  }

  const handleAfterRestoreDefaultLayout = (gridApi: GridApi) => {
    gridApi.setColumnsVisible(
      [DealsGridColumnId.COLLATERAL_ISIN_OR_BUI],
      defaultCollateralIdentifierType !== DefaultCollateralIdentifierType.CUSIP
    )

    gridApi.setColumnsVisible(
      [DealsGridColumnId.COLLATERAL_CUSIP],
      defaultCollateralIdentifierType !== DefaultCollateralIdentifierType.ISIN
    )
  }

  return (
    <CenterPanel>
      <StructuredHeroPanel dealDetails={structuredRows} />
      <DataGrid
        columnDefs={columnDefs}
        customToolbarSwitches={customToolbarSwitches}
        gridColumnAutoSizing={GridAutoSizing.IGNORE_HEADERS}
        gridId={GridId.StructuredGrid}
        gridOptions={structuredGridOptions}
        GroupByToggles={DealsGroupByToggles}
        rowData={structuredRows}
        subscriptionFilters={() => (
          <DealSubscriptionFiltersComponent
            dealSubscriptionIdentifier={DealSubscriptionIdentifier.STRUCTURED}
            subscriptionDetail={subscriptionDetail}
          />
        )}
        subscriptionDetail={subscriptionDetail}
        onPageChanged={onPageChanged}
        showLoader={!snapshotLoaded}
        onAfterRestoreDefaultLayout={handleAfterRestoreDefaultLayout}
        saveSelectedItemIds={true}
        exportFileName={generateExportFileName('Structured')}
      />
      <RelatedPanel dealDetails={relatedDealDetails} parentGridId={GridId.StructuredGrid} onClose={handleRelatedOnClose} />
    </CenterPanel>
  )
}
